import { useEffect, useState } from "react";
import type { AuditFacets, AuditFilterState } from "./types";
import { EMPTY_FILTERS, hasActiveFilters } from "./types";
import "./AuditFilterBar.css";

interface AuditFilterBarProps {
  filters: AuditFilterState;
  onFiltersChange: (next: AuditFilterState) => void;
  facets: AuditFacets | null;
  /** If true, render an Organization select populated from facets.orgs. */
  showOrgFilter?: boolean;
  /** Optional right-aligned status like "Showing 25 of 342". */
  countLabel?: string;
}

export function AuditFilterBar({
  filters,
  onFiltersChange,
  facets,
  showOrgFilter = false,
  countLabel,
}: AuditFilterBarProps) {
  // Debounce free-text so we don't hit the API on every keystroke.
  const [localQ, setLocalQ] = useState(filters.q);
  useEffect(() => setLocalQ(filters.q), [filters.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (localQ !== filters.q) {
        onFiltersChange({ ...filters, q: localQ });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQ]);

  const patch = (partial: Partial<AuditFilterState>) =>
    onFiltersChange({ ...filters, ...partial });

  const clearAll = () => {
    setLocalQ("");
    onFiltersChange({ ...EMPTY_FILTERS });
  };

  return (
    <div className="audit-filter-bar">
      <div className="audit-filter-bar-row">
        <div className="audit-filter-search">
          <input
            type="text"
            placeholder="Search action, resource, or metadata..."
            value={localQ}
            onChange={(e) => setLocalQ(e.target.value)}
            className="audit-filter-search-input"
            aria-label="Search activity"
          />
          {localQ && (
            <button
              type="button"
              className="audit-filter-search-clear"
              onClick={() => setLocalQ("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {countLabel && (
          <span className="audit-filter-count">{countLabel}</span>
        )}
      </div>

      <div className="audit-filter-bar-row audit-filter-bar-row--controls">
        <label className="audit-filter-field">
          <span className="audit-filter-label">Action</span>
          <select
            value={filters.action}
            onChange={(e) => patch({ action: e.target.value })}
            disabled={!facets}
          >
            <option value="">All</option>
            {facets?.actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>

        <label className="audit-filter-field">
          <span className="audit-filter-label">Resource</span>
          <select
            value={filters.resourceType}
            onChange={(e) => patch({ resourceType: e.target.value })}
            disabled={!facets}
          >
            <option value="">All</option>
            {facets?.resourceTypes.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>

        <label className="audit-filter-field">
          <span className="audit-filter-label">User</span>
          <select
            value={filters.userId}
            onChange={(e) => patch({ userId: e.target.value })}
            disabled={!facets}
          >
            <option value="">All</option>
            {facets?.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </select>
        </label>

        {showOrgFilter && (
          <label className="audit-filter-field">
            <span className="audit-filter-label">Organization</span>
            <select
              value={filters.orgId}
              onChange={(e) => patch({ orgId: e.target.value })}
              disabled={!facets?.orgs}
            >
              <option value="">All</option>
              {facets?.orgs?.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>
        )}

        <label className="audit-filter-field">
          <span className="audit-filter-label">From</span>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => patch({ from: e.target.value })}
          />
        </label>

        <label className="audit-filter-field">
          <span className="audit-filter-label">To</span>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => patch({ to: e.target.value })}
          />
        </label>

        {hasActiveFilters(filters) && (
          <button
            type="button"
            className="audit-filter-clear-all"
            onClick={clearAll}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
